import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChartArea, Clipboard, EllipsisVertical, Link } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import {
	Dropdown,
	DropdownButton,
	DropdownItem,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import { TableBody, TableCell, TableRow } from "@/components/catalyst/Table";
import { EmptyState } from "@/components/EmptyState";
import { orpc } from "@/orpc/client";
import { AppStatistics } from "./components/AppStatistics";
import { ReferralLinksLayout } from "./ReferralLinksLayout";

export function ReferralLinks() {
	const queryClient = useQueryClient();
	const { appId } = useParams({ from: "/_app/apps/$appId/" });
	const navigate = useNavigate();
	const search = useSearch({ from: "/_app/apps/$appId/" });

	const cursors = search.c;

	const setCursors = (updater: string[] | ((prev: string[]) => string[])) => {
		navigate({
			to: "/apps/$appId",
			params: { appId },
			search: (prev) => ({
				...prev,
				tab: prev.tab as "referral_links" | "settings",
				c:
					typeof updater === "function"
						? updater((prev.c as string[]) ?? [])
						: updater,
			}),
		});
	};

	const currentCursor =
		cursors.length > 0 ? cursors[cursors.length - 1] : undefined;

	const { data } = useSuspenseQuery(
		orpc.referralLinks.list.queryOptions({
			input: {
				appId,
				limit: 10,
				cursor: currentCursor,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.referralLinks.update.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.referralLinks.list.key(),
				});
				toast.success("Referral link updated");
			},
		}),
	);

	const removeMutation = useMutation(
		orpc.referralLinks.remove.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.referralLinks.list.key(),
				});
				toast.success("Referral link removed");
			},
		}),
	);

	const resendApprovalMutation = useMutation(
		orpc.referralLinks.resendApproval.mutationOptions({
			onSuccess: () => {
				toast.success("Approval email resent to partner");
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const requestTerminationMutation = useMutation(
		orpc.referralLinks.requestTermination.mutationOptions({
			onSuccess: () => {
				toast.success("Termination request sent to partner");
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const handleNext = () => {
		if (data.nextCursor) {
			setCursors((prev) => [...prev, data.nextCursor ?? ""]);
		}
	};

	const handlePrevious = () => {
		if (cursors.length > 0) {
			setCursors((prev) => prev.slice(0, -1));
		}
	};

	const handleCreate = () => {
		navigate({
			to: "/apps/$appId/links/create",
			params: { appId },
		});
	};

	return (
		<>
			<AppStatistics appId={appId} />
			<ReferralLinksLayout
				onCreateClick={handleCreate}
				onNext={handleNext}
				onPrevious={handlePrevious}
				hasPrevious={cursors.length > 0}
				hasNext={!!data.nextCursor}
				isEmpty={data.items.length === 0}
				emptyState={
					<EmptyState
						icon={Link}
						title="No referral links found"
						subtitle="Create a referral link to start tracking partner attributions."
					/>
				}
			>
				<TableBody>
					{data.items.map((link) => (
						<TableRow key={link.id}>
							<TableCell className="font-medium text-base-content">
								{link.partnerName}
							</TableCell>
							<TableCell className="font-medium text-base-content">
								<div className="flex items-center gap-2">
									<div>{link.slug}</div>
									<button
										type="button"
										className="text-base-content/40 hover:text-base-content transition-colors cursor-pointer"
										aria-label="Copy code"
										onClick={() => navigator.clipboard.writeText(link.slug)}
									>
										<Clipboard className="size-4" />
									</button>
								</div>
							</TableCell>
							<TableCell className="text-base-content/60 capitalize">
								{link.planHint || "Any"}
							</TableCell>
							<TableCell className="text-base-content/60">
								{link.redemptionCount}
								{link.maxRedemptions ? `/${link.maxRedemptions}` : "/∞"}
							</TableCell>
							<TableCell>
								<Badge color={link.isActive ? "emerald" : "zinc"}>
									{link.isActive ? "Active" : "Inactive"}
								</Badge>
							</TableCell>
							<TableCell>
								<Badge color={link.partnerAgreedAt ? "emerald" : "amber"}>
									{link.partnerAgreedAt ? "Approved" : "Pending"}
								</Badge>
							</TableCell>
							<TableCell className="text-base-content/60">
								{new Date(link.createdAt).toLocaleDateString("en-GB", {
									day: "2-digit",
									month: "short",
									year: "numeric",
								})}
							</TableCell>
							<TableCell>
								<div className="flex items-center justify-end gap-3">
									<Button
										plain
										className="cursor-pointer"
										aria-label="View analytics"
										href={`/apps/${appId}/links/${link.id}/data`}
									>
										<ChartArea className="size-5" />
									</Button>
									<Dropdown>
										<DropdownButton
											plain
											aria-label="More options"
											className="cursor-pointer"
										>
											<EllipsisVertical className="size-5" />
										</DropdownButton>
										<DropdownMenu anchor="bottom end">
											<DropdownItem href={`/apps/${appId}/links/${link.id}`}>
												View / Edit
											</DropdownItem>
											{!link.partnerAgreedAt && (
												<DropdownItem
													onClick={() =>
														resendApprovalMutation.mutate({ id: link.id })
													}
												>
													Resend approval email
												</DropdownItem>
											)}
											<DropdownItem onClick={() => alert("TODO")}>
												Attach store codes
											</DropdownItem>
											<DropdownItem
												onClick={() =>
													updateMutation.mutate({
														id: link.id,
														isActive: !link.isActive,
													})
												}
											>
												{link.isActive ? "Disable" : "Enable"}
											</DropdownItem>
											{link.partnerAgreedAt &&
												!link.deletionStatus.isDeletable && (
													<DropdownItem
														onClick={() => {
															if (
																confirm(
																	"This will email the partner to request termination of the agreement. Continue?",
																)
															) {
																requestTerminationMutation.mutate({
																	id: link.id,
																});
															}
														}}
													>
														Request deletion from partner
													</DropdownItem>
												)}
											<DropdownItem
												disabled={!link.deletionStatus.isDeletable}
												title={link.deletionStatus.reason ?? undefined}
												onClick={() => {
													if (!link.deletionStatus.isDeletable) return;
													if (
														confirm(
															"Are you sure you want to delete this referral link?",
														)
													) {
														removeMutation.mutate({ id: link.id });
													}
												}}
											>
												Delete
											</DropdownItem>
										</DropdownMenu>
									</Dropdown>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</ReferralLinksLayout>
		</>
	);
}
